import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import Main from '../../../component/Main.jsx';
import Loading from '../../../component/common/loading/Loading.jsx';
import ExcelDownload from '../../../component/common/button/ExcelDownload.jsx';
import CommonSelect from './CommonSelect09.jsx';
import Pagination from '../../../component/common/dataGrid/Pagination.jsx';

import { useDynamicStyle } from '../../../hooks/useDynamicStyle.js';
import * as Icons from '../../../images/icons/icons_component.js';
import * as Theme from '../../../images/icons/icons_theme';
import { useExcelDownloadHook } from '../../common/excel/useExcelDownloadHook.js';
import useExcelConfirmYn from '../../common/excel/useExcelConfirmYn.js';
import ExcelDownloadReason_layer from '../../../component/common/modal/ExcelDownloadReason_layer.jsx';

export default function BdTrmsHumanNurse() {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = `${today.getMonth() + 1}`.padStart(2, '0');

    const [searchParams, setSearchParams] = useState({
        searchFromdate: '',
        searchCheck2: currentMonth,
        searchYear: currentYear,
        searchGubun: 'N',
        searchEmog: '',
        searchType: '',
        temp: '01'
    });

    const [dataList, setDataList] = useState([]);
    const [clone, setClone] = useState([]);
    const [checkItems, setCheckItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searched, setSearched] = useState(false);
    const [all, setAll] = useState([]);
    const [desc, setDesc] = useState('');

    const [pageInfo, setPageInfo] = useState({
        paginationTF: true,
        currentPage: 1,
        itemsPerPage: 200,
        totalItems: 0,
    });

    const popupPropsInfo = {
        localStorageName: "tableData",
        popupAddress: "/nedis/trmsInfo/trms_humanNurse_popup.do",
    };

    const DATA_HEAD = ['의료기관명', '근무 부서', '성명', '외상센터 근무 시작일', '외상센터 근무 종료일', '비고'];

    const DATA_COLUMN = [
        'EMOGDESC',
        'WORK_DEPT_CD',
        'NM',
        'BEGIN_DE',
        'END_DE',
        'ETC_RM'
    ];

    const API_URL = '/trmsapi/obthrc/get_org_detail_tab_resource_list_nurse.do';
    const API_URL_ALL = '/trmsapi/obthrc/get_org_detail_tab_resource_list_nurse_all.do';
    const CNT_API_URL = '/trmsapi/obthrc/get_org_detail_tab_resource_cnt_nurse.do';
    const EXCEL_FILE_NAME = `${desc || '전체'}_간호사 인력현황`;

    const COLUMN_OPTIONS = {
        WORK_DEPT_CD: [
            { value: '외상소생구역', label: '외상소생구역' },
            { value: '외상중환자실', label: '외상중환자실' },
            { value: '외상입원실', label: '외상입원실' },
        ],
        DEFAULT_YN: [
            { value: '', label: '' },
            { value: 'Y', label: 'Y' },
        ],
    };

    const singleHeader = !Array.isArray(DATA_HEAD[0]);
    const headerRows = singleHeader ? [DATA_HEAD] : DATA_HEAD;
    const headerRowCount = headerRows.length;
    const colCount = headerRows[0].length;
    const skip = Array.from({ length: headerRowCount }, () => Array(colCount).fill(false));

    const getHeaderSpans = (r, c) => {
        if (skip[r][c]) return null;
        const value = headerRows[r][c];
        let rowspan = 1;
        let colspan = 1;

        // horizontal
        for (let i = c + 1; i < colCount; i++) {
            if (headerRows[r][i] === value) {
                colspan++;
                skip[r][i] = true;
            } else break;
        }

        // vertical
        if (!singleHeader) {
            for (let i = r + 1; i < headerRowCount; i++) {
                let same = true;
                for (let j = 0; j < colspan; j++) {
                    if (headerRows[i][c + j] !== value) {
                        same = false;
                        break;
                    }
                }
                if (same) {
                    for (let j = 0; j < colspan; j++) skip[i][c + j] = true;
                    rowspan++;
                } else break;
            }
        }

        return { rowspan, colspan, value };
    };

    const computeRowSpans = useCallback((list) => {
        const mergeKey = 'EMOGDESC';
        const spans = list.map(() => ({}));

        let prev = null;
        let start = 0;
        let cnt = 1;

        list.forEach((row, idx) => {
            const cur = row[mergeKey];
            if (cur === prev) {
                spans[start][mergeKey] += 1;
                spans[idx][mergeKey] = 0;
                cnt += 1;
            } else {
                spans[idx][mergeKey] = 1;
                start = idx;
                cnt = 1;
                prev = cur;
            }
        });

        list.forEach((_, i) => {
            DATA_COLUMN.forEach((k) => {
                if (!(k in spans[i])) spans[i][k] = 1;
            });
        });
        return spans;
    }, []);

    const rowSpans = useMemo(() => computeRowSpans(dataList), [dataList, computeRowSpans]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const { currentPage, itemsPerPage } = pageInfo;
            const params = { ...searchParams, pageNum: currentPage, displayRowsize: itemsPerPage };
            const { data } = await axios.get(API_URL, { params });
            setDataList(data);
            setClone(data);
            if (CNT_API_URL === '') {
                setPageInfo(prev => ({
                    ...prev,
                    totalItems: data.length
                }));
            } else {
                fetchDataCnt();
            }
            setSearched(true);
        } catch (err) {
            console.error('데이터 로딩 오류:', err);
        } finally {
            setLoading(false);
        }
    }, [searchParams, pageInfo]);

    const fetchAllData = async () => {
        try {
            const response = await axios.get(API_URL_ALL,
                { params: searchParams }
            );
            setAll(response.data);
        } catch (error) {
            console.error(error);
        }
    };

    const fetchDataCnt = async () => {
        try {
            const response = await axios.get(CNT_API_URL,
                { params: searchParams }
            );
            setPageInfo(prev => ({
                ...prev,
                totalItems: response.data
            }));
        } catch (error) {
            console.error('데이터 카운터 중 오류 발생:', error);
        }
    };

    const handleInputChange = (e) => {
        const { name, type, value, checked } = e.target;
        if (name === 'searchGubun') {
            const gubun = checked ? 'Y' : 'N';
            setSearchParams(prev => ({
                ...prev,
                searchGubun: gubun,
                searchFromdate: prev.temp,
                temp: gubun === 'N' ? prev.searchFromdate : ''
            }));
            return;
        }
        setSearchParams(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleAllCheck = (checked) => setCheckItems(checked ? [...clone] : []);

    const handleSingleCheck = (checked, item) => {
        const originalItem = clone.find((row) => row.SEQ === item.SEQ);
        if (!originalItem) return;

        setCheckItems((prev) =>
            checked
                ? [...prev, originalItem]
                : prev.filter((i) => i.SEQ !== item.SEQ)
        );
    };

    const handleCellChange = (rowIdx, colKey, value) => {
        setDataList((prev) => {
            const next = [...prev];
            const currentRow = next[rowIdx];

            if (currentRow[colKey] === value) return prev;

            let newState = currentRow.stateRows;

            if (newState !== 'inserted') {
                newState = 'updated';
            }

            const updatedRow = {
                ...currentRow,
                [colKey]: value,
                stateRows: newState,
            };

            next[rowIdx] = updatedRow;
            return next;
        });
    };

    const handlePageChange = (page) => {
        if (page !== pageInfo.currentPage) setPageInfo((prev) => ({ ...prev, currentPage: page }));
    };

    const search = () => {
        setPageInfo(prev => ({
            ...prev,
            currentPage: 1,
        }));
        fetchData();
        setCheckItems([]);
    };

    const convertUpdatedRowsToJson = (updateRows) => {
        const jsonArray = updateRows.map((row) => {
            const {
                WORK_DEPT_CD,
                EMOGCODE,
                DETAIL_SN,
                NM,
                LCNS_NO,
                BEGIN_DE,
                END_DE,
                ETC_RM
            } = row;

            return {
                workDeptCd: WORK_DEPT_CD || '',
                emogCode: EMOGCODE || '',
                nm: NM || '',
                detailSn: DETAIL_SN || '',
                lcnsNo: LCNS_NO || '',
                beginDe: BEGIN_DE || '',
                endDe: END_DE || '',
                etcRm: ETC_RM || '',
                delFlag: ''
            };
        });
        return jsonArray;
    };

    const convertDeletedRowsToJson = (checkRows) => {
        const deleted = checkRows.map((row) => ({
            workDeptCd: '',
            emogCode: row.EMOGCODE || '',
            nm: '',
            detailSn: row.DETAIL_SN || '',
            lcnsNo: '',
            beginDe: '',
            endDe: '',
            etcRm: '',
            delFlag: 'Y'
        }));
        console.log(deleted);
        return deleted;
    };

    const save = async () => {
        const updatedRows = dataList.filter(row => row.stateRows === 'updated');
        const jsonArray = convertUpdatedRowsToJson(updatedRows);
        const deleted = convertDeletedRowsToJson(checkItems);

        const combined = [...jsonArray, ...deleted];
        const payload = new URLSearchParams({
            emogcode: searchParams.searchEmog,
            hnfData: JSON.stringify(combined),
            userId: localStorage.getItem("id"),
        });

        try {
            const response = await fetch('/trmsapi/obthrc/update_hnf_data_list_nurse.do', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: payload,
            });

            if (!response.ok) {
                throw new Error(`서버 오류: ${response.status}`);
            }

            const resultText = await response.text();
            console.log('저장 결과:', resultText);

            alert('저장이 완료되었습니다.');
        } catch (error) {
            console.error('저장 중 오류 발생:', error);
            alert('저장에 실패했습니다. 관리자에게 문의하세요.');
        }
    };

    const download = async () => {
        try {
            const response = await axios.get('/trmsapi/obthrc/download_nurse.do', {
                responseType: 'blob',
            });

            const blob = new Blob([response.data], { type: response.headers['content-type'] });
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;

            const disposition = response.headers['content-disposition'];
            const match = disposition && disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
            const filename = match ? decodeURIComponent(match[1].replace(/['"]/g, '')) : 'template.xlsx';

            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            console.error('다운로드 실패:', error);
        }
    };

    const upload = () => {
        if (searchParams.searchEmog === '') {
            alert("기관을 선택해주세요.");
            return false;
        } else {
            const senData = {
                tableOwner: 'TRUMAUSER',
                tableName: 'TRAUMA_NRS_HNF_T',
                head: ['근무 부서', '성명', '면허번호', '근무 시작일', '근무 종료일', '비고'],
                emogCode: searchParams.searchEmog,
            }

            let popup = null;

            localStorage.setItem(popupPropsInfo.localStorageName, JSON.stringify(senData));
            popup = window.open(popupPropsInfo.popupAddress, "팝업", "scrollbars=1, resizable=0, width=1200, height=755, top=30, left=30");
        }
    };

    const renderCell = (row, rowIdx, colKey) => {
        const span = rowSpans[rowIdx][colKey];
        if (span === 0) return null;
        const value = row[colKey] ?? '';

        if (['BEGIN_DE', 'END_DE'].includes(colKey)) {
            return (
                <td key={colKey} rowSpan={span} className='text-center'>
                    <input type="date" value={value} onChange={(e) => handleCellChange(rowIdx, colKey, e.target.value)} />
                </td>
            );
        }

        if (['EMOGDESC', 'NM', 'ETC_RM'].includes(colKey)) {
            return (
                <td key={colKey} rowSpan={span}>
                    <input
                        type="text"
                        value={value}
                        className={`h-7 bg-transparent truncate ${colKey !== 'ETC_RM' ? 'text-center outline-none' : ''}`}
                        onChange={(e) => handleCellChange(rowIdx, colKey, e.target.value)}
                    />
                </td>
            );
        }

        const opts = COLUMN_OPTIONS[colKey] || COLUMN_OPTIONS.DEFAULT_YN;
        return (
            <td key={colKey} rowSpan={span}>
                <select value={value} onChange={(e) => handleCellChange(rowIdx, colKey, e.target.value)}>
                    {opts.map((o) => (
                        <option key={o.value} value={o.value}>
                            {o.label}
                        </option>
                    ))}
                </select>
            </td>
        );
    };

    useEffect(() => {
        search();
        fetchAllData();
    }, []);

    useEffect(() => {
        fetchData();
    }, [pageInfo.currentPage]);

    const { fontColor, lightBackColor, borderColorGray } = useDynamicStyle();

    const confirmYn = useExcelConfirmYn();
    const path = decodeURIComponent(useLocation().pathname);
    const { isModalOpen, isDown, updateParams, openConfModal, closeModal, handleUpdateInputChange, setIsDown } = useExcelDownloadHook();

    return (
        <Main>
            <ExcelDownloadReason_layer
                isModalOpen={isModalOpen}
                closeModal={closeModal}
                updateParams={updateParams}
                handleUpdateInputChange={handleUpdateInputChange}
                searchParams={searchParams}
                route={path}
                excelFileName={`${EXCEL_FILE_NAME}.xlsx`}
                setIsDown={setIsDown}
            />
            <div className="com-search">
                <table>
                    <colgroup>
                        <col width="10%" />
                        <col width="29%" />
                        <col width="10%" />
                        <col width="20%" />
                        <col width="10%" />
                        <col width="20%" />
                    </colgroup>
                    <tbody>
                        <CommonSelect searchParams={searchParams} handleInputChange={handleInputChange} setDesc={setDesc} />
                        <tr>
                            <th>엑셀 업로드</th>
                            <td className='flex'>
                                <button onClick={upload} className='search_btn bg_tr_primary'><Icons.Upload16w />엑셀업로드</button>
                                <button onClick={download} className='excel_form_down'><Theme.Download16 className='co_black' />양식다운로드</button>
                            </td>
                            <th>근무 부서</th>
                            <td>
                                <select
                                    id='searchType'
                                    name='searchType'
                                    value={searchParams.searchType}
                                    onChange={handleInputChange}
                                >
                                    <option value="">전체</option>
                                    <option value="외상소생구역">외상소생구역</option>
                                    <option value="외상중환자실">외상중환자실</option>
                                    <option value="외상입원실">외상입원실</option>
                                </select>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="search_btn_wrapper">
                <span className='total_items'>전체&nbsp;<strong className={fontColor}>{pageInfo.totalItems}</strong>건</span>
                <div className='btn_container'>
                    <button onClick={search} disabled={loading} className='search_btn bg_tr_primary'><Icons.Search16w />조회</button>
                    <button onClick={save} disabled={loading} className='search_btn bg_co_black'><Icons.Save16w />저장</button>
                    <ExcelDownload
                        excelHead={DATA_HEAD}
                        excelData={searchParams.searchEmog ? clone : all}
                        searched={searched}
                        dataCode={DATA_COLUMN}
                        excelFileName={EXCEL_FILE_NAME}
                        confirmYn={confirmYn}
                        openConfModal={openConfModal}
                        isDown={isDown}
                    />
                </div>
            </div>

            {loading ? <Loading />
                : (
                    <div className='data_grid_container'>
                        <hr className={`border-t-2 mr-1-1 ${borderColorGray}`} />
                        <div className={`data_list_table ${searched ? 'grid-animation' : ''}`}>
                            <table>
                                <thead className='middle'>
                                    {headerRows.map((row, rIdx) => (
                                        <tr key={rIdx}>
                                            {rIdx === 0 && (
                                                <th
                                                    rowSpan={headerRowCount}
                                                    className='bg_co_white align-middle'
                                                >
                                                    <input type="checkbox" checked={dataList.length > 0 && checkItems.length === dataList.length} onChange={(e) => handleAllCheck(e.target.checked)} />
                                                </th>
                                            )}
                                            {row.map((col, cIdx) => {
                                                const span = getHeaderSpans(rIdx, cIdx);
                                                if (!span) return null;
                                                const { rowspan, colspan, value } = span;
                                                return (
                                                    <th key={`${rIdx}-${cIdx}`} rowSpan={rowspan} colSpan={colspan}>
                                                        {value}
                                                    </th>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </thead>
                                <tbody>
                                    {dataList.map((row, rowIdx) => {
                                        const isChecked = checkItems.some((i) => i.SEQ === row.SEQ);
                                        const oe = rowIdx % 2 === 0 ? lightBackColor : '';
                                        return (
                                            <tr key={row.SEQ || rowIdx} className={oe}>
                                                <td>
                                                    <input type="checkbox" checked={isChecked} onChange={(e) => handleSingleCheck(e.target.checked, row)} />
                                                </td>
                                                {DATA_COLUMN.map((colKey) => renderCell(row, rowIdx, colKey))}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <hr className='mr-1-1 co_border_gray' />
                        {dataList.length > 0 && pageInfo.paginationTF && (
                            <div>
                                <Pagination paginationInfo={{ ...pageInfo, onPageChange: handlePageChange }} />
                            </div>
                        )}
                    </div>
                )}
        </Main>
    );
}
